import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, EyeIcon, EyeSlashIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { resetAdminPassword } from "../api/adminsApi";
import type { Admin } from "../types";

type ResetPasswordModalProps = {
    isOpen: boolean;
    onClose: () => void;
    admin: Admin | null;
};

export const ResetPasswordModal = ({ isOpen, onClose, admin }: ResetPasswordModalProps) => {
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});

    const resetMutation = useMutation({
        mutationFn: ({ adminId, password }: { adminId: string; password: string }) =>
            resetAdminPassword(adminId, { newPassword: password }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admins"] });
            onClose();
            resetForm();
        },
    });

    const resetForm = () => {
        setNewPassword("");
        setConfirmPassword("");
        setErrors({});
        setShowPassword(false);
    };

    const validateForm = (): boolean => {
        const newErrors: { newPassword?: string; confirmPassword?: string } = {};

        if (!newPassword) {
            newErrors.newPassword = "Password is required";
        } else if (newPassword.length < 8) {
            newErrors.newPassword = "Password must be at least 8 characters";
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = "Please confirm the password";
        } else if (confirmPassword !== newPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm() && admin) {
            resetMutation.mutate({ adminId: admin.adminId, password: newPassword });
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-2xl transition-all dark:bg-slate-900">
                                <div className="flex items-center justify-between mb-6">
                                    <Dialog.Title className="text-xl font-semibold text-slate-900 dark:text-white">
                                        Reset Admin Password
                                    </Dialog.Title>
                                    <button
                                        onClick={handleClose}
                                        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Warning Banner */}
                                <div className="mb-6 flex items-start gap-3 rounded-lg bg-amber-50 p-4 dark:bg-amber-900/20">
                                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
                                    <div>
                                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                                            Security Notice
                                        </p>
                                        <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                                            This will immediately change the password for this admin. They will need
                                            to use the new password for their next login.
                                        </p>
                                    </div>
                                </div>

                                {admin && (
                                    <div className="mb-6 rounded-lg bg-slate-50 p-4 dark:bg-slate-800">
                                        <p className="text-sm text-slate-600 dark:text-slate-400">
                                            Resetting password for:{" "}
                                            <span className="font-medium text-slate-900 dark:text-white">
                                                {admin.fullName}
                                            </span>
                                        </p>
                                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                                            {admin.email}
                                        </p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value);
                                                    if (errors.newPassword) {
                                                        setErrors((prev) => ({ ...prev, newPassword: undefined }));
                                                    }
                                                }}
                                                className={`w-full rounded-lg border px-4 py-2.5 pr-10 text-sm transition focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-white ${errors.newPassword
                                                        ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/20"
                                                        : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/20 dark:border-slate-700"
                                                    }`}
                                                placeholder="Enter new password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                            >
                                                {showPassword ? (
                                                    <EyeSlashIcon className="h-5 w-5" />
                                                ) : (
                                                    <EyeIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                        {errors.newPassword && (
                                            <p className="mt-1 text-xs text-rose-500">{errors.newPassword}</p>
                                        )}
                                        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                            Minimum 8 characters. Recommended: uppercase, lowercase, digit, special character
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                            Confirm Password
                                        </label>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                if (errors.confirmPassword) {
                                                    setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                                                }
                                            }}
                                            className={`w-full rounded-lg border px-4 py-2.5 text-sm transition focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-white ${errors.confirmPassword
                                                    ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/20"
                                                    : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/20 dark:border-slate-700"
                                                }`}
                                            placeholder="Confirm new password"
                                        />
                                        {errors.confirmPassword && (
                                            <p className="mt-1 text-xs text-rose-500">{errors.confirmPassword}</p>
                                        )}
                                    </div>

                                    {resetMutation.isError && (
                                        <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-600 dark:bg-rose-900/20 dark:text-rose-400">
                                            {(resetMutation.error as any)?.response?.data?.message ||
                                                "Failed to reset password. Please try again."}
                                        </div>
                                    )}

                                    <div className="flex gap-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={resetMutation.isPending}
                                            className="flex-1 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {resetMutation.isPending ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                            fill="none"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                        />
                                                    </svg>
                                                    Resetting...
                                                </span>
                                            ) : (
                                                "Reset Password"
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
